/**
 * Core Web Vitals reporting.
 *
 * `web-vitals` is loaded with a dynamic `import()` — and only for sessions that
 * fall inside the deterministic sample — so unsampled sessions pay nothing.
 *
 * Wire format (POST `endpoint`, `application/json`):
 *
 *   { "vitals": [ { app, name, value, rating, delta, id, navigation_type,
 *                   route, session_id, build, ts } ] }
 *
 * `route` is a templated route ({@link scrubRoute}); it never carries a query,
 * hash, or raw token/id segment.
 */

import { EventQueue } from "../queue";
import { isSessionSampled } from "../sampling";
import { scrubRoute } from "../scrub";
import { getSessionId } from "../session";
import type { SendFn } from "../types";
import { createBeaconSend, createFetchSend } from "./transport";

export const WEB_VITAL_NAMES = ["LCP", "INP", "CLS", "FCP", "TTFB"] as const;
export type WebVitalName = (typeof WEB_VITAL_NAMES)[number];

export interface WebVitalEvent {
  app: string;
  name: string;
  value: number;
  rating: string;
  delta: number;
  id: string;
  navigation_type?: string;
  route: string;
  session_id: string;
  build?: string;
  ts: string;
}

/** The subset of a `web-vitals` `Metric` we read. */
export interface VitalMetricLike {
  name: string;
  value: number;
  rating: string;
  delta: number;
  id: string;
  navigationType?: string;
}

type VitalCallback = (metric: VitalMetricLike) => void;

/** The subset of the `web-vitals` module we call. */
export interface WebVitalsModule {
  onLCP: (cb: VitalCallback) => void;
  onINP: (cb: VitalCallback) => void;
  onCLS: (cb: VitalCallback) => void;
  onFCP: (cb: VitalCallback) => void;
  onTTFB: (cb: VitalCallback) => void;
}

export interface ReportWebVitalsOptions {
  /** POST target for the vitals batch. */
  endpoint: string;
  /** Route templates; see {@link scrubRoute}. */
  routeTemplates?: readonly string[];
  /** Fraction of SESSIONS reporting (0..1). Default 0.1. */
  sampleRate?: number;
  app?: string;
  buildSha?: string;
  /** Override the session id (defaults to the shared per-tab session id). */
  sessionId?: string;
  /** Test seam: replaces the dynamic `import("web-vitals")`. */
  loadWebVitals?: () => Promise<WebVitalsModule>;
  /** Test seam: replaces the fetch-based transport. */
  send?: SendFn;
}

export interface WebVitalsHandle {
  /** Flush queued vitals now (fetch transport). Returns the number accepted. */
  flush: () => Promise<number>;
  /** Remove the page-hide listeners. */
  stop: () => void;
}

const DEFAULT_SAMPLE_RATE = 0.1;
const SAMPLE_SALT = "web-vitals";

function round(value: number, name: string): number {
  const factor = name === "CLS" ? 10_000 : 100;
  return Math.round(value * factor) / factor;
}

/**
 * Start reporting Core Web Vitals. Resolves to `null` when this session is not
 * sampled or there is no DOM; otherwise to a handle.
 */
export async function reportWebVitals(options: ReportWebVitalsOptions): Promise<WebVitalsHandle | null> {
  if (typeof window === "undefined" || typeof document === "undefined") return null;

  const sessionId = options.sessionId ?? getSessionId();
  const rate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
  if (!isSessionSampled(sessionId, rate, SAMPLE_SALT)) return null;

  let vitals: WebVitalsModule;
  try {
    vitals = options.loadWebVitals ? await options.loadWebVitals() : ((await import("web-vitals")) as WebVitalsModule);
  } catch {
    return null; // never let a missing chunk break the host app
  }

  const fetchSend = options.send ?? createFetchSend({ endpoint: options.endpoint });
  const beaconSend = options.send ?? createBeaconSend({ endpoint: options.endpoint, fallback: fetchSend });
  const queue = new EventQueue<WebVitalEvent>({
    fingerprintOf: () => undefined,
    withRepeat: (item) => item,
    encode: (encoded) => `{"vitals":[${encoded.join(",")}]}`,
  });

  const flushWith = (send: SendFn) => (): Promise<number> => queue.flush(send);
  const flush = flushWith(fetchSend);
  const flushOnHide = flushWith(beaconSend);

  const record: VitalCallback = (metric) => {
    queue.push({
      app: options.app ?? "",
      name: metric.name,
      value: round(metric.value, metric.name),
      rating: metric.rating,
      delta: round(metric.delta, metric.name),
      id: metric.id,
      ...(metric.navigationType ? { navigation_type: metric.navigationType } : {}),
      route: scrubRoute(window.location.href, { templates: options.routeTemplates }),
      session_id: sessionId,
      ...(options.buildSha ? { build: options.buildSha } : {}),
      ts: new Date().toISOString(),
    });
    // INP / CLS finalise on hide; anything reported after that has no later chance.
    if (document.visibilityState === "hidden") void flushOnHide();
  };

  const onVisibility = (): void => {
    if (document.visibilityState === "hidden") void flushOnHide();
  };
  const onPageHide = (): void => {
    void flushOnHide();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);

  vitals.onLCP(record);
  vitals.onINP(record);
  vitals.onCLS(record);
  vitals.onFCP(record);
  vitals.onTTFB(record);

  return {
    flush,
    stop: () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    },
  };
}
